// HTTP header values must be ISO-8859-1 (latin1). Anything above U+00FF makes
// `fetch` throw before the request is even sent:
//
//   Failed to read the 'headers' property from 'RequestInit':
//   String contains non ISO-8859-1 code point.
//
// Several of our telemetry headers carry free text that we don't control -
// x-vis-name (a chart/board/goal name), x-carrier-name, x-vis-url.
// AI-generated chart titles are the most reliable source of trouble (em dashes
// and curly quotes are its house style), but a carrier or a hand-typed
// dashboard called "O'Brien - West" breaks exactly the same way, and takes
// every GraphQL query on the page down with it.
//
// Accented Latin is FINE - U+00E9 sits inside latin1. Only codepoints above
// U+00FF are a problem.
//
// Every non-ASCII character below is written as a \u escape on purpose. Typed
// literally, the invisible ones are unreviewable - and a literal NUL makes git
// classify this whole file as binary, so its diffs stop showing up in review.

// Fold the typography that actually shows up into ASCII lookalikes, so the
// telemetry stays readable rather than becoming a row of dropped characters.
const LOOKALIKES: [RegExp, string][] = [
  // hyphens, en/em dashes, minus sign
  [/[\u2010-\u2015\u2212]/g, '-'],
  // curly single quotes
  [/[\u2018\u2019\u201A\u201B]/g, "'"],
  // curly double quotes
  [/[\u201C\u201D\u201E\u201F]/g, '"'],
  // ellipsis
  [/\u2026/g, '...'],
  // arrows
  [/[\u2192\u27A1]/g, '->'],
  // non-breaking, zero-width and ideographic spaces
  [/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' '],
];

// Make a string safe to send as an HTTP header value. Lossy by design - this is
// telemetry, so a readable approximation beats a thrown request.
const toHeaderSafe = (value: string): string => {
  const folded = LOOKALIKES.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value,
  );
  // Keep only codepoints a header value can actually carry: printable latin1.
  // Done by codepoint rather than regex for two reasons - a negated range that
  // includes NUL trips eslint's no-control-regex, and spreading iterates whole
  // codepoints, so an emoji is dropped as a unit instead of leaving half a
  // surrogate pair behind.
  //
  // This also strips control characters, which matters beyond tidiness: a CR or
  // LF in a header value is rejected by fetch just as hard as a non-latin1 one,
  // and is a header-injection vector besides.
  return [...folded]
    .filter((char) => {
      const code = char.codePointAt(0) ?? 0;
      return code >= 0x20 && code <= 0xff;
    })
    .join('');
};

export default toHeaderSafe;
